import {
  checkAnswer,
  confirmationActions,
  describeAnswer,
  liveQuestion,
  tapValue,
  type ChatItem,
  type ConfirmationCard,
  type ConversationLanguage,
  type ConfirmationRow,
  type QuestionDefinition,
  type QuestionItem,
} from '@cyberscore/contracts';
import {
  Check,
  CheckCircle2,
  Keyboard,
  ListChecks,
  Lock,
  PartyPopper,
  ScanText,
  Send,
  Sparkles,
  Upload,
} from 'lucide-react';
import { createContext, useContext, useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { AnswerInput, showsValueError } from '../assessments/AnswerInput';
import { plainMessage } from '../assessments/flow';
import { acceptAttr, acceptedKinds, fileProblem } from '../documents/files';
import { THREAD_COPY } from './copy';

/** The thread's own words, in the conversation's language. */
const Copy = createContext(THREAD_COPY.en);

/** What the person can do in the thread. The screen decides what each does (save, call the core, …). */
export interface ChatHandlers {
  /** A quick reply or inline form: the person's own answer, saved straight away. */
  answer: (question: string, value: unknown, text: string, via: 'tapped' | 'form') => void;
  confirm: (card: string, questions: string[]) => void;
  /** "Change": nothing on the card is saved and the question is asked again. */
  decline: (card: string, questions: string[]) => void;
  skip: (question: string) => void;
  /** A file for an upload question: stored, scanned, then saved as the answer. Absent: uploads are off. */
  upload?: (question: string, file: File) => void;
}

interface ThreadProps {
  items: readonly ChatItem[];
  /** Definitions of the questions in the thread, for quick replies and form cards. */
  questions: Readonly<Record<string, QuestionDefinition>>;
  handlers: ChatHandlers;
  /** Shown as a typing indicator, e.g. "Reading your reply". */
  busy?: string | null;
  /** Keep the newest message in view (off for static examples). */
  scroll?: boolean;
  /** Nothing can be answered or confirmed, e.g. a submitted assessment. */
  readOnly?: boolean;
  /** The conversation's language, for the thread's buttons and labels. */
  language?: ConversationLanguage;
}

/**
 * The conversation. Only the live question shows its quick replies or form, so earlier questions can't
 * be answered out of order from the transcript; open confirmation cards stay actionable until expired.
 */
export function ChatThread({
  items,
  questions,
  handlers,
  busy,
  scroll = true,
  readOnly = false,
  language = 'en',
}: ThreadProps) {
  const live = readOnly ? null : liveQuestion(items);
  const log = useRef<HTMLDivElement>(null);
  const shown = useRef(false);
  // Scroll the log, not the page, so the newest message is in view: at once on opening, smoothly after.
  useEffect(() => {
    const el = log.current;
    if (!scroll || !el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: shown.current ? 'smooth' : 'instant' });
    shown.current = true;
  }, [items.length, busy, scroll]);

  return (
    <Copy.Provider value={THREAD_COPY[language]}>
      <div
        ref={log}
        className="chat-log"
        role="log"
        aria-live="polite"
        aria-relevant="additions"
        aria-busy={!!busy}
      >
        {items.map((it) => (
          <Message
            key={it.id}
            item={it}
            live={!busy && live?.id === it.id}
            disabled={!!busy || readOnly}
            questions={questions}
            handlers={handlers}
          />
        ))}
        {busy && <Typing label={busy} />}
      </div>
    </Copy.Provider>
  );
}

function Message({
  item,
  live,
  disabled,
  questions,
  handlers,
}: {
  item: ChatItem;
  live: boolean;
  disabled: boolean;
  questions: ThreadProps['questions'];
  handlers: ChatHandlers;
}) {
  const t = useContext(Copy);
  switch (item.kind) {
    case 'bot':
      return (
        <Bot>
          <p>{item.text}</p>
          {item.score_delta !== null && item.score_delta !== 0 && (
            <span className={`pts ${item.score_delta > 0 ? 'up' : 'down'}`}>
              {t.points(item.score_delta)}
            </span>
          )}
          {item.sources.length > 0 && (
            <p className="chat-sources">
              {t.basedOn}: {item.sources.map((x) => x.title).join(' · ')}
            </p>
          )}
        </Bot>
      );
    case 'user':
      return (
        <div className="msg me">
          <div className="bub">{item.text}</div>
          {item.via !== 'typed' && <div className="via">{item.via === 'tapped' ? t.tapped : t.form}</div>}
        </div>
      ); // prettier-ignore
    case 'question': {
      const q = questions[item.question];
      return (
        <Bot>
          {item.stage && <div className="chat-topic">{item.stage.name}</div>}
          <p>
            {item.again && `${t.again} `}
            {item.text}
            {item.optional && <span className="badge optional">{t.optional}</span>}
          </p>
          {item.why && <p className="why">{item.why}</p>}
          {item.kill_switch && (
            <KillSwitchNote>{t.killSwitchQuestion}</KillSwitchNote>
          )}
          {live && q && <Answering item={item} q={q} handlers={handlers} />}
        </Bot>
      ); // prettier-ignore
    }
    case 'confirmation':
      return <Confirmation card={item} disabled={disabled} handlers={handlers} />;
    case 'stage':
      return (
        <Bot>
          <div className="chat-milestone">
            <CheckCircle2 size={16} />
            <div>
              <b>{t.stageDone(item.name)}</b>
              {item.message && <p>{item.message}</p>}
            </div>
          </div>
        </Bot>
      );
    case 'done':
      return (
        <Bot>
          <div className="chat-milestone">
            <PartyPopper size={16} />
            <div>
              <b>{t.doneTitle(item.required_left)}</b>
              <p>{t.doneText(item.answered, item.required_left)}</p>
            </div>
          </div>
        </Bot>
      );
  }
}

function Bot({ children, wide }: { children: React.ReactNode; wide?: boolean }) {
  return (
    <div className={`msg bot${wide ? ' wide' : ''}`}>
      <div className="bot-av" aria-hidden="true">
        <Sparkles size={14} />
      </div>
      <div className="bub">{children}</div>
    </div>
  );
}

function KillSwitchNote({ children }: { children: React.ReactNode }) {
  return (
    <div className="ks">
      <Lock size={14} aria-hidden="true" />
      <span>{children}</span>
    </div>
  );
}

export function Typing({ label }: { label: string }) {
  return (
    <div className="msg bot" role="status">
      <div className="bot-av" aria-hidden="true">
        <Sparkles size={14} />
      </div>
      <div className="bub typing">
        <span className="dots" aria-hidden="true">
          <i />
          <i />
          <i />
        </span>
        {label}…
      </div>
    </div>
  );
}

/** Upload a file as the answer: checked here first, then stored and scanned by the core. */
function UploadReply({
  item,
  q,
  upload,
  skip,
}: {
  item: QuestionItem;
  q: QuestionDefinition;
  upload: (question: string, file: File) => void;
  skip: React.ReactNode;
}) {
  const t = useContext(Copy);
  const input = useRef<HTMLInputElement>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const accept = (q.settings as { accept?: string[] }).accept;
  return (
    <div className="qr">
      <input
        ref={input}
        type="file"
        className="sr-only"
        tabIndex={-1}
        accept={acceptAttr(accept)}
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (!f) return;
          const p = fileProblem(f, accept);
          setProblem(p);
          if (!p) upload(item.question, f);
        }}
      />
      <button type="button" className="chip" onClick={() => input.current?.click()}>
        <Upload size={14} /> {t.upload}
      </button>
      {skip}
      <p className="why full">{t.uploadHint(acceptedKinds(accept))}</p>
      {problem && (
        <div className="error full" role="alert">
          {problem}
        </div>
      )}
    </div>
  );
}

/** The live question's answering controls, by how the question is asked in chat. */
function Answering({
  item,
  q,
  handlers,
}: {
  item: QuestionItem;
  q: QuestionDefinition;
  handlers: ChatHandlers;
}) {
  const t = useContext(Copy);
  const [form, setForm] = useState<'shown' | 'opened' | null>(
    item.presentation === 'inline_form' || item.presentation === 'date_picker' ? 'shown' : null,
  );
  const skip = item.optional && (
    <button type="button" className="chip ghost" onClick={() => handlers.skip(item.question)}>
      {t.skip}
    </button>
  );

  if (item.presentation === 'quick_replies')
    return item.multiple ? (
      <MultiReplies item={item} q={q} handlers={handlers} skip={skip} />
    ) : (
      <div className="qr" role="group" aria-label={t.quickReplies}>
        {item.quick_replies.map((r) => (
          <button
            key={r.value}
            type="button"
            className="chip"
            onClick={() => handlers.answer(item.question, tapValue(q, [r.value]), r.label, 'tapped')}
          >
            {r.label}
          </button>
        ))}
        {skip}
      </div>
    ); // prettier-ignore

  if (item.presentation === 'upload')
    return handlers.upload ? (
      <UploadReply item={item} q={q} upload={handlers.upload} skip={skip} />
    ) : (
      <div className="qr">
        <button type="button" className="chip" disabled title={t.uploadLater(false)}>
          <Upload size={14} /> {t.upload}
        </button>
        {skip}
        <p className="why full">{t.uploadLater(item.optional)}</p>
      </div>
    ); // prettier-ignore

  if (form) return <InlineForm item={item} q={q} handlers={handlers} skip={skip} focus={form === 'opened'} />;

  // Typed: the composer is the input; the form is there for anyone who prefers it.
  return (
    <div className="qr">
      <span className="hint-line">
        <Keyboard size={14} /> {t.typeBelow}
      </span>
      <button type="button" className="chip ghost" onClick={() => setForm('opened')}>
        {t.useForm}
      </button>
      {skip}
    </div>
  );
}

function MultiReplies({
  item,
  q,
  handlers,
  skip,
}: {
  item: QuestionItem;
  q: QuestionDefinition;
  handlers: ChatHandlers;
  skip: React.ReactNode;
}) {
  const t = useContext(Copy);
  const [picked, setPicked] = useState<string[]>([]);
  const value = tapValue(q, picked);
  const check = picked.length ? checkAnswer(q, value, 'USER_DECLARED') : null;
  return (
    <div className="qr" role="group" aria-label={t.pickAll}>
      {item.quick_replies.map((r) => {
        const on = picked.includes(r.value);
        return (
          <button
            key={r.value}
            type="button"
            className={`chip${on ? ' on' : ''}`}
            aria-pressed={on}
            onClick={() => setPicked(on ? picked.filter((v) => v !== r.value) : [...picked, r.value])}
          >
            {on && <Check size={14} />}
            {r.label}
          </button>
        );
      })}
      <button
        type="button"
        className="chip primary"
        disabled={!check?.ok}
        onClick={() => handlers.answer(item.question, value, describeAnswer(q, value), 'tapped')}
      >
        {t.done(picked.length)}
      </button>
      {skip}
      {check && !check.ok && <p className="error full">{plainMessage(check.errors[0]!.message)}</p>}
    </div>
  );
}

/** A form card inside the conversation, for answers that chips and free text don't suit. */
function InlineForm({
  item,
  q,
  handlers,
  skip,
  focus,
}: {
  item: QuestionItem;
  q: QuestionDefinition;
  handlers: ChatHandlers;
  skip: React.ReactNode;
  /** Opened on request: move focus into it, which also brings it into view. */
  focus?: boolean;
}) {
  const t = useContext(Copy);
  const [draft, setDraft] = useState<unknown>(undefined);
  const ref = useRef<HTMLFormElement>(null);
  useEffect(() => {
    if (focus) ref.current?.querySelector<HTMLElement>('input, select, textarea, button')?.focus();
  }, [focus]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const save = (value: unknown) => {
    if (value === undefined) return setErrors({ '': t.enterFirst });
    const check = checkAnswer(q, value, 'USER_DECLARED');
    if (!check.ok)
      return setErrors(Object.fromEntries(check.errors.map((e) => [e.path, plainMessage(e.message)])));
    handlers.answer(item.question, check.value, describeAnswer(q, check.value), 'form');
  };
  const id = `chat-${item.id}`;
  return (
    <form
      ref={ref}
      className="chat-form"
      noValidate
      aria-label={item.text}
      onSubmit={(e) => {
        e.preventDefault();
        save(draft);
      }}
    >
      <AnswerInput id={id} q={q} value={draft} onChange={setDraft} onPick={save} errors={errors} />
      {errors[''] && !showsValueError(q) && <div className="error">{errors['']}</div>}
      <div className="chat-form-foot">
        {skip}
        <button type="submit" className="btn btn-sm btn-primary">
          {t.saveAnswer}
        </button>
      </div>
    </form>
  ); // prettier-ignore
}

const ROW_TONE: Record<Exclude<ConfirmationRow['state'], 'open'>, string> = {
  confirmed: 'ok',
  declined: 'neutral',
  expired: 'neutral',
};

function Confirmation({
  card,
  disabled,
  handlers,
}: {
  card: ConfirmationCard;
  disabled: boolean;
  handlers: ChatHandlers;
}) {
  const t = useContext(Copy);
  const { own, together } = confirmationActions(card);
  const open = card.rows.filter((r) => r.state === 'open');
  const doc = card.source.kind === 'document' ? card.source.name : null;
  const many = card.rows.length > 1;
  return (
    <Bot wide>
      <div className={`cc${open.length ? '' : ' closed'}`} role="group" aria-label={doc ? t.foundIn(doc) : t.understood}>
        <div className="cc-h">
          {doc ? <ScanText size={15} /> : <ListChecks size={15} />}
          {doc ? t.foundIn(doc) : t.understood}
        </div>
        {card.rows.map((r) => (
          <div key={r.question} className={`cc-row ${r.state}`}>
            <div className="grow">
              <div className="cc-q">{r.question_text}</div>
              {r.quote && <div className="quote">“{r.quote}”</div>}
            </div>
            <div className="cc-a">
              <b>{r.label}</b>
              <span className={r.confidence < 0.85 ? 'unsure' : undefined}>
                {t.sure(Math.round(r.confidence * 100))}{r.page ? ` · ${t.page(r.page)}` : ''}
              </span>
              {r.state !== 'open' ? (
                <span className={`pill ${ROW_TONE[r.state]}`}>{t.rowState[r.state]}</span>
              ) : (
                own.includes(r.question) && (
                  <button type="button" className="btn btn-sm" disabled={disabled} onClick={() => handlers.confirm(card.id, [r.question])}>
                    <Check size={14} /> {t.confirm}
                  </button>
                )
              )}
            </div>
          </div>
        ))}
        {open.some((r) => r.kill_switch) && (
          <KillSwitchNote>{t.killSwitchCard}</KillSwitchNote>
        )}
        {open.length > 0 && (
          <div className="cc-f">
            <span className="muted grow">
              {own.length && !together.length
                ? t.footer.ownOnly
                : own.length
                  ? t.footer.mixed
                  : doc
                    ? t.footer.document
                    : t.footer.chat}
            </span>
            <button type="button" className="btn btn-sm" disabled={disabled} onClick={() => handlers.decline(card.id, open.map((r) => r.question))}>
              {t.change}
            </button>
            {together.length > 0 && (
              <button type="button" className="btn btn-sm btn-primary" disabled={disabled} onClick={() => handlers.confirm(card.id, together)}>
                <Check size={14} />
                {/* Rows confirmed together never need their own confirmation, so they are the optional answers. */}
                {t.confirmLabel(own.length, together.length, many)}
              </button>
            )}
          </div>
        )}
      </div>
    </Bot>
  ); // prettier-ignore
}

/** Where the person types. Enter sends; Shift+Enter starts a new line. */
export function Composer({
  onSend,
  disabled,
  maxLength,
  language = 'en',
  initial = '',
}: {
  onSend: (text: string) => void;
  disabled?: boolean;
  maxLength: number;
  language?: ConversationLanguage;
  /** Text to start with, e.g. a message that couldn't be sent. */
  initial?: string;
}) {
  const copy = THREAD_COPY[language];
  const [text, setText] = useState(initial);
  const send = () => {
    const t = text.trim();
    if (!t || disabled) return;
    onSend(t);
    setText('');
  };
  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      send();
    }
  };
  return (
    <form
      className="composer"
      onSubmit={(e) => {
        e.preventDefault();
        send();
      }}
    >
      <textarea
        className="input"
        rows={1}
        aria-label={copy.yourReply}
        placeholder={copy.placeholder}
        maxLength={maxLength}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={onKey}
      />
      <button type="submit" className="btn btn-primary btn-icon-round" aria-label={copy.send} disabled={disabled || !text.trim()}>
        <Send size={16} />
      </button>
    </form>
  ); // prettier-ignore
}
